import type { ComponentProps, FunctionComponent, ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import Select, {
  components,
  type GroupType,
  type OptionProps,
  type OptionType,
  type StylesConfig,
} from '@atlaskit/select';
import { useIntl } from 'react-intl';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Lozenge from '@atlaskit/lozenge/new';
import { token } from '@atlaskit/tokens';

import SiteTile from '../../../Tile/SiteTile/SiteTile';
import {
  getAvatarIconColor,
  getSiteTileColor,
  type SiteTileColor,
} from '../../../Tile/siteTileColors';
import {
  getTrelloWorkspaceUrl,
  type TrelloWorkspace,
} from '../domain/trelloTypes';
import {
  compareTrelloWorkspacesByEnterpriseThenName,
  compareTrelloWorkspacesByName,
  hasEnterpriseTrelloWorkspace,
  isTrelloWorkspaceSelectable,
  sortTrelloWorkspacesByName,
} from '../domain/trelloWorkspaceSelection';
import FieldLabel from '../MultiSelect/FieldLabel';
import { trelloWorkspaceSelectorMessages } from './messages';

export const TRELLO_MULTI_SELECT_INPUT_ID =
  'oauth-consent-mcp-trello-workspace-multi-select';

const wrapperStyles = xcss({ width: '100%', alignSelf: 'stretch' });

const stackStyles = xcss({ width: '100%' });

const groupLabelStyles = xcss({
  paddingBlock: 'space.050',
  paddingInline: 'space.025',
});

const disabledOptionStyles = xcss({
  cursor: 'not-allowed',
  opacity: 0.62,
});

export type WorkspaceOption = OptionType & {
  workspaceId: string;
  workspace: TrelloWorkspace;
  url: string | null;
  avatarUrl?: string;
};

const toOption = (workspace: TrelloWorkspace): WorkspaceOption => ({
  value: workspace.id,
  label: workspace.displayName,
  workspaceId: workspace.id,
  workspace,
  url: getTrelloWorkspaceUrl(workspace),
  avatarUrl: workspace.logoUrl ?? undefined,
});

const byWorkspaceName = (
  options: readonly WorkspaceOption[],
): WorkspaceOption[] =>
  [...options].sort((a, b) =>
    compareTrelloWorkspacesByName(a.workspace, b.workspace),
  );

const byEnterpriseThenWorkspaceName = (
  options: readonly WorkspaceOption[],
): WorkspaceOption[] =>
  [...options].sort((a, b) =>
    compareTrelloWorkspacesByEnterpriseThenName(a.workspace, b.workspace),
  );

/**
 * Splits options into a "Recently used" group and an "All" group.
 *
 * Each workspace appears in exactly one group: anything surfaced as recent is
 * removed from the group below it, so a workspace is never listed twice.
 * Recency decides which group a workspace lands in, not where it sits inside
 * that group. "All" leads with the Enterprise-owned workspaces and then the
 * rest, each block alphabetical; "Recently used" is alphabetical throughout,
 * since it is a short list where grouping by ownership would not help.
 */
export function groupWorkspaceOptionsByRecency({
  options,
  recentWorkspaceIds,
  recentLabel,
  allLabel,
}: {
  options: readonly WorkspaceOption[];
  recentWorkspaceIds: readonly string[];
  recentLabel: string;
  allLabel: string;
}): GroupType<WorkspaceOption>[] {
  const allGroup = {
    label: allLabel,
    options: byEnterpriseThenWorkspaceName(options),
  };

  if (recentWorkspaceIds.length === 0) {
    return [allGroup];
  }

  const optionsById = new Map(
    options.map((option) => [option.workspaceId, option]),
  );
  const recentIds = new Set(
    recentWorkspaceIds.filter((id) => optionsById.has(id)),
  );

  if (recentIds.size === 0) {
    return [allGroup];
  }

  const recentOptions = byWorkspaceName(
    [...recentIds].map((id) => optionsById.get(id) as WorkspaceOption),
  );
  const remainingOptions = byEnterpriseThenWorkspaceName(
    options.filter((option) => !recentIds.has(option.workspaceId)),
  );

  if (remainingOptions.length === 0) {
    return [{ label: recentLabel, options: recentOptions }];
  }

  return [
    { label: recentLabel, options: recentOptions },
    { label: allLabel, options: remainingOptions },
  ];
}

const chipColor = (option: OptionType): SiteTileColor => {
  const { avatarUrl, label } = option as Partial<WorkspaceOption>;
  return getAvatarIconColor(avatarUrl) ?? getSiteTileColor(String(label ?? ''));
};

const Option = (
  props: OptionProps<WorkspaceOption, true> & { isWorkspaceSelected?: boolean },
): ReactElement => {
  const { formatMessage } = useIntl();
  const { label, url, avatarUrl, workspace } = props.data;
  const isSelected = props.isWorkspaceSelected ?? props.isSelected;
  const isEnterprise = workspace.atlassianOrganizationId != null;

  return (
    <components.Option {...props} isSelected={isSelected}>
      <Inline
        space="space.100"
        alignBlock="center"
        xcss={props.isDisabled ? disabledOptionStyles : undefined}
      >
        <input
          type="checkbox"
          checked={isSelected}
          disabled={props.isDisabled}
          readOnly
          tabIndex={-1}
          aria-hidden
        />
        <SiteTile avatarUrl={avatarUrl} name={String(label ?? '')} />
        <Stack>
          <Inline space="space.075" alignBlock="center">
            <Text weight="medium">{label}</Text>
            {isEnterprise && (
              <Lozenge appearance="neutral">
                {formatMessage(trelloWorkspaceSelectorMessages.enterpriseBadge)}
              </Lozenge>
            )}
          </Inline>
          {url && (
            <Text size="small" color="color.text.subtlest">
              {url}
            </Text>
          )}
        </Stack>
      </Inline>
    </components.Option>
  );
};

const MultiValueLabel = (
  props: ComponentProps<typeof components.MultiValueLabel>,
): ReactElement => {
  const { label, url } = props.data as Partial<WorkspaceOption>;
  return (
    <components.MultiValueLabel {...props}>
      {url ? `${String(label)} | ${url}` : String(label)}
    </components.MultiValueLabel>
  );
};

const multiValueStyles: StylesConfig<WorkspaceOption, true> = {
  multiValue: (base, state) => {
    const color = chipColor(state.data);
    return {
      ...base,
      backgroundColor: color.background,
      borderColor: color.border,
      borderStyle: 'solid',
      borderWidth: token('border.width', '1px'),
    };
  },
  multiValueLabel: (base, state) => ({
    ...base,
    color: chipColor(state.data).foreground,
  }),
  multiValueRemove: (base, state) => ({
    ...base,
    color: chipColor(state.data).foreground,
  }),
};

const formatGroupLabel = (group: GroupType<WorkspaceOption>): ReactElement => (
  <Box xcss={groupLabelStyles}>
    <Text size="small" color="color.text.subtlest" weight="bold">
      {group.label}
    </Text>
  </Box>
);

export interface TrelloWorkspaceMultiSelectProps {
  availableWorkspaces: readonly TrelloWorkspace[];
  selectedWorkspaces: readonly TrelloWorkspace[];
  setSelectedWorkspaces: (workspaces: TrelloWorkspace[]) => void;
  /** Workspace ids to surface under "Recently used", most recent first. */
  recentWorkspaceIds?: readonly string[];
  /** Whether to render the field label above the select. */
  showHeading?: boolean;
  /**
   * Maximum number of workspaces the user may select. When set, help text is
   * shown below the field and unselected options are disabled once the limit
   * is reached. Omit or pass null for an unbounded selection.
   */
  maxSelectableWorkspaces?: number | null;
  /** Name of the requesting client, used in the selection limit help text. */
  clientName?: string | null;
  /** True when another page of workspaces can be fetched. */
  hasMoreWorkspaces?: boolean;
  /** True while the next page is in flight. */
  isLoadingMoreWorkspaces?: boolean;
  /** Called when the menu is scrolled to the bottom and more pages remain. */
  onLoadMoreWorkspaces?: () => void;
}

/**
 * Multi-workspace selector for the Trello MCP consent screen.
 *
 * Renders grouped, checkbox-style options with workspace avatars and URLs, and
 * shows the current selection as removable chips. Mirrors the Rovo site
 * selector so both consent surfaces stay visually consistent.
 */
const TrelloWorkspaceMultiSelect: FunctionComponent<
  TrelloWorkspaceMultiSelectProps
> = ({
  availableWorkspaces,
  selectedWorkspaces,
  setSelectedWorkspaces,
  recentWorkspaceIds = [],
  showHeading = false,
  maxSelectableWorkspaces = null,
  clientName = null,
  hasMoreWorkspaces = false,
  isLoadingMoreWorkspaces = false,
  onLoadMoreWorkspaces,
}) => {
  const { formatMessage } = useIntl();

  const selectionLimit =
    maxSelectableWorkspaces != null &&
    Number.isFinite(maxSelectableWorkspaces) &&
    maxSelectableWorkspaces > 0
      ? Math.floor(maxSelectableWorkspaces)
      : null;

  // The connection has no sort argument, so ordering is applied to the whole
  // accumulated list rather than per page. Later pages are merged into the
  // alphabetical sequence instead of being appended to the bottom.
  const options = useMemo(
    () => sortTrelloWorkspacesByName(availableWorkspaces).map(toOption),
    [availableWorkspaces],
  );

  const groupedOptions = useMemo(
    () =>
      groupWorkspaceOptionsByRecency({
        options,
        recentWorkspaceIds,
        recentLabel: formatMessage(
          trelloWorkspaceSelectorMessages.recentGroupLabel,
        ),
        allLabel: formatMessage(trelloWorkspaceSelectorMessages.allGroupLabel),
      }),
    [options, recentWorkspaceIds, formatMessage],
  );

  const selectedIds = useMemo(
    () => new Set(selectedWorkspaces.map((workspace) => workspace.id)),
    [selectedWorkspaces],
  );

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedIds.has(option.workspaceId)),
    [options, selectedIds],
  );

  const TrelloOption = useMemo(
    () =>
      (props: OptionProps<WorkspaceOption, true>): ReactElement => (
        <Option
          {...props}
          isWorkspaceSelected={selectedIds.has(props.data.workspaceId)}
        />
      ),
    [selectedIds],
  );

  const isSelectionLimitReached =
    selectionLimit != null && selectedIds.size >= selectionLimit;

  const isOptionDisabled = useCallback(
    (option: WorkspaceOption): boolean => {
      if (selectedIds.has(option.workspaceId)) {
        return false;
      }
      if (isSelectionLimitReached) {
        return true;
      }
      return !isTrelloWorkspaceSelectable({
        workspace: option.workspace,
        selectedWorkspaces,
        isMultiSelectEnabled: true,
      });
    },
    [isSelectionLimitReached, selectedIds, selectedWorkspaces],
  );

  const isEnterpriseMember = useMemo(
    () => hasEnterpriseTrelloWorkspace(availableWorkspaces),
    [availableWorkspaces],
  );

  const limitHelpText =
    selectionLimit == null
      ? null
      : clientName
        ? formatMessage(
            trelloWorkspaceSelectorMessages.selectionLimitHelpText,
            {
              limit: selectionLimit,
              clientName,
            },
          )
        : formatMessage(
            trelloWorkspaceSelectorMessages.selectionLimitHelpTextNoClient,
            { limit: selectionLimit },
          );

  // Both messages occupy the same slot below the field. Enterprise members are
  // shown the combination rule instead of the limit, since it is the
  // constraint that explains why options are disabled.
  const helpText = isEnterpriseMember
    ? formatMessage(trelloWorkspaceSelectorMessages.enterpriseSelectionHelpText)
    : limitHelpText;

  return (
    <Box xcss={wrapperStyles}>
      <Stack space="space.050" xcss={stackStyles}>
        {showHeading && (
          <Box as="label" htmlFor={TRELLO_MULTI_SELECT_INPUT_ID}>
            <FieldLabel>
              {formatMessage(trelloWorkspaceSelectorMessages.multiSelectLabel)}
            </FieldLabel>
          </Box>
        )}
        <Select<WorkspaceOption, true>
          inputId={TRELLO_MULTI_SELECT_INPUT_ID}
          options={groupedOptions}
          isMulti
          isSearchable
          isClearable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          placeholder={formatMessage(
            trelloWorkspaceSelectorMessages.placeholder,
          )}
          value={selectedOptions}
          components={{
            Option: TrelloOption,
            MultiValueLabel:
              MultiValueLabel as typeof components.MultiValueLabel,
          }}
          formatGroupLabel={formatGroupLabel}
          styles={multiValueStyles}
          isOptionDisabled={isOptionDisabled}
          onMenuScrollToBottom={() => {
            if (hasMoreWorkspaces && !isLoadingMoreWorkspaces) {
              onLoadMoreWorkspaces?.();
            }
          }}
          isLoading={isLoadingMoreWorkspaces}
          loadingMessage={() =>
            formatMessage(trelloWorkspaceSelectorMessages.loadingMoreWorkspaces)
          }
          onChange={(nextOptions, actionMeta) => {
            const chosen = (nextOptions ?? []) as readonly WorkspaceOption[];
            const toggled = actionMeta.option as WorkspaceOption | undefined;

            // Selecting an already-selected workspace from the other group
            // means the user intends to deselect it.
            if (
              (actionMeta.action === 'select-option' ||
                actionMeta.action === 'deselect-option') &&
              toggled &&
              selectedIds.has(toggled.workspaceId)
            ) {
              setSelectedWorkspaces(
                selectedWorkspaces.filter(
                  (workspace) => workspace.id !== toggled.workspaceId,
                ),
              );
              return;
            }

            const uniqueById = new Map(
              chosen.map((option) => [option.workspaceId, option.workspace]),
            );
            const nextWorkspaces = [...uniqueById.values()];
            setSelectedWorkspaces(
              selectionLimit == null
                ? nextWorkspaces
                : nextWorkspaces.slice(0, selectionLimit),
            );
          }}
        />
        {helpText && (
          <Text as="p" size="small" color="color.text.subtle">
            {helpText}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export default TrelloWorkspaceMultiSelect;
